/**
 * WorkflowEditorPage — Tier 4.2 Workflow Editor Admin UI
 *
 * Allows admins to:
 * - List all workflows from workflow_registry
 * - Create new workflow definitions (JSON editor)
 * - Edit/update workflow definitions
 * - View step graph in a visual breakdown
 * - Execute workflows with test input
 * - View execution results
 * - Deprecate/activate workflows
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  GitBranch,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Eye,
  Code,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────

interface Workflow {
  id: string;
  workflowName: string;
  version: string;
  domainId: string | null;
  definition: WorkflowDefinition;
  status: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowDefinition {
  name: string;
  description?: string;
  trigger: {
    type: string;
    entitySlug?: string;
    event?: string;
  };
  steps: WorkflowStep[];
  entryStep: string;
  maxDurationMs?: number;
  maxSteps?: number;
}

interface WorkflowStep {
  id: string;
  type: string;
  label?: string;
  config: Record<string, unknown>;
  next?: string;
  onTrue?: string;
  onFalse?: string;
  branches?: string[];
}

interface ExecutionResult {
  workflowId: string;
  workflowName: string;
  status: string;
  steps: StepResult[];
  output: Record<string, unknown>;
  durationMs: number;
}

interface StepResult {
  stepId: string;
  status: string;
  output?: Record<string, unknown>;
  error?: string;
  durationMs: number;
}

// ─── Component ───────────────────────────────────

export default function WorkflowEditorPage() {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editorJson, setEditorJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showExecute, setShowExecute] = useState(false);
  const [executeInput, setExecuteInput] = useState('{}');
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [executing, setExecuting] = useState(false);

  // New workflow form
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newDefinition, setNewDefinition] = useState(DEFAULT_DEFINITION);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Workflow[]>('/workflows?limit=100');
      setWorkflows(data);
    } catch { setWorkflows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const selected = workflows.find((w) => w.id === selectedId);

  const handleSelect = (wf: Workflow) => {
    setSelectedId(wf.id);
    setEditorJson(JSON.stringify(wf.definition, null, 2));
    setEditing(false);
    setExecutionResult(null);
  };

  const handleCreate = async () => {
    if (!newName) return;
    setSaving(true);
    try {
      const def = JSON.parse(newDefinition);
      await api.post('/workflows', {
        workflowName: newName,
        version: '1.0.0',
        domainId: newDomain || undefined,
        definition: def,
        status: 'active',
      });
      setShowCreate(false);
      setNewName('');
      setNewDefinition(DEFAULT_DEFINITION);
      fetchWorkflows();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create workflow');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const def = JSON.parse(editorJson);
      await api.patch(`/workflows/${selectedId}`, { definition: def });
      setEditing(false);
      fetchWorkflows();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleDeprecate = async (id: string) => {
    if (!confirm('Deprecate this workflow?')) return;
    try {
      await api.delete(`/workflows/${id}`);
      fetchWorkflows();
    } catch { /* ignore */ }
  };

  const handleActivate = async (id: string) => {
    try {
      await api.post(`/workflows/${id}/activate`, {});
      fetchWorkflows();
    } catch { /* ignore */ }
  };

  const handleExecute = async () => {
    if (!selectedId) return;
    setExecuting(true);
    setExecutionResult(null);
    try {
      const input = JSON.parse(executeInput);
      const result = await api.post<ExecutionResult>(`/workflows/${selectedId}/execute`, { input });
      setExecutionResult(result);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Execution failed');
    } finally { setExecuting(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitBranch className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Workflow Editor</h1>
            <p className="text-sm text-muted-foreground">
              Create, edit, and execute DB-defined workflows
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchWorkflows}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
            <Plus className="mr-1 h-4 w-4" /> New Workflow
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="border-dashed border-primary/40 bg-primary/5">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium">Workflow Name</label>
                <Input className="mt-1" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="order-approval" />
              </div>
              <div>
                <label className="text-xs font-medium">Domain</label>
                <Input className="mt-1" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="operations" />
              </div>
              <div className="flex items-end gap-2">
                <Button size="sm" onClick={handleCreate} disabled={saving || !newName}>
                  {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                  Create
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Definition (JSON)</label>
              <textarea
                className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                rows={12}
                value={newDefinition}
                onChange={(e) => setNewDefinition(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {/* Workflow List */}
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground">Workflows ({workflows.length})</h2>
            {workflows.map((wf) => (
              <div
                key={wf.id}
                className={cn(
                  'cursor-pointer rounded-md border px-3 py-2 transition-colors hover:bg-muted/50',
                  selectedId === wf.id && 'border-primary bg-primary/5',
                )}
                onClick={() => handleSelect(wf)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{wf.workflowName}</span>
                  <StatusBadge status={wf.status} />
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <span>v{wf.version}</span>
                  {wf.domainId && <span>• {wf.domainId}</span>}
                  <span>• {wf.definition?.steps?.length ?? 0} steps</span>
                </div>
              </div>
            ))}
            {workflows.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No workflows. Create one to get started.
              </p>
            )}
          </div>

          {/* Workflow Detail */}
          <div className="col-span-2 space-y-4">
            {selected ? (
              <>
                {/* Actions bar */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">{selected.workflowName}</h2>
                    <p className="text-xs text-muted-foreground">
                      ID: {selected.id} • v{selected.version}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {selected.status === 'active' ? (
                      <Button variant="outline" size="sm" onClick={() => handleDeprecate(selected.id)}>
                        <Pause className="mr-1 h-4 w-4" /> Deprecate
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => handleActivate(selected.id)}>
                        <Play className="mr-1 h-4 w-4" /> Activate
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowExecute(!showExecute); setExecutionResult(null); }}
                    >
                      <Zap className="mr-1 h-4 w-4" /> {showExecute ? 'Hide Execute' : 'Execute'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(!editing)}
                    >
                      <Code className="mr-1 h-4 w-4" /> {editing ? 'Preview' : 'Edit JSON'}
                    </Button>
                  </div>
                </div>

                {/* Step Graph (visual breakdown) */}
                {!editing && selected.definition?.steps && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Step Flow</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StepGraph
                        steps={selected.definition.steps}
                        entryStep={selected.definition.entryStep}
                        executionSteps={executionResult?.steps}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* JSON Editor */}
                {editing && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-base">Definition (JSON)</CardTitle>
                      <Button size="sm" onClick={handleUpdate} disabled={saving}>
                        {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                        Save Changes
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <textarea
                        className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                        rows={20}
                        value={editorJson}
                        onChange={(e) => setEditorJson(e.target.value)}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Execute Panel */}
                {showExecute && (
                  <Card className="border-dashed border-primary/40">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-base">Execute Workflow</CardTitle>
                      <Button size="sm" onClick={handleExecute} disabled={executing}>
                        {executing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
                        Run
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <label className="text-xs font-medium">Input (JSON)</label>
                        <textarea
                          className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                          rows={4}
                          value={executeInput}
                          onChange={(e) => setExecuteInput(e.target.value)}
                          placeholder='{"entityId": "...", "amount": 1500}'
                        />
                      </div>

                      {executionResult && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={executionResult.status} />
                            <span className="text-xs text-muted-foreground">
                              {executionResult.durationMs}ms • {executionResult.steps.length} steps executed
                            </span>
                          </div>

                          {/* Step results */}
                          <div className="space-y-1">
                            {executionResult.steps.map((sr) => (
                              <div
                                key={sr.stepId}
                                className={cn(
                                  'flex items-center justify-between rounded px-2 py-1 text-xs',
                                  sr.status === 'success' && 'bg-green-50 dark:bg-green-950/30',
                                  sr.status === 'failed' && 'bg-red-50 dark:bg-red-950/30',
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  {sr.status === 'success' ? (
                                    <CheckCircle className="h-3 w-3 text-green-500" />
                                  ) : (
                                    <XCircle className="h-3 w-3 text-red-500" />
                                  )}
                                  <span className="font-mono">{sr.stepId}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span>{sr.durationMs}ms</span>
                                  {sr.error && <span className="text-red-600">{sr.error}</span>}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Output */}
                          <details>
                            <summary className="text-xs font-medium cursor-pointer">Output</summary>
                            <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto max-h-40">
                              {JSON.stringify(executionResult.output, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
                Select a workflow to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'active' || status === 'success'
    ? 'default'
    : status === 'deprecated' || status === 'failed'
    ? 'destructive'
    : 'secondary';

  return <Badge variant={variant}>{status}</Badge>;
}

function StepGraph({
  steps,
  entryStep,
  executionSteps,
}: {
  steps: WorkflowStep[];
  entryStep: string;
  executionSteps?: StepResult[];
}) {
  const stepMap = new Map(steps.map((s) => [s.id, s]));
  const execMap = new Map(executionSteps?.map((s) => [s.stepId, s]));

  // Build linear traversal order from entryStep
  const visited = new Set<string>();
  const ordered: WorkflowStep[] = [];

  function traverse(id: string) {
    if (visited.has(id)) return;
    visited.add(id);
    const step = stepMap.get(id);
    if (!step) return;
    ordered.push(step);
    if (step.next) traverse(step.next);
    if (step.onTrue) traverse(step.onTrue);
    if (step.onFalse) traverse(step.onFalse);
    if (step.branches) step.branches.forEach(traverse);
  }
  traverse(entryStep);

  // Add any un-visited steps
  for (const step of steps) {
    if (!visited.has(step.id)) ordered.push(step);
  }

  return (
    <div className="space-y-1">
      {ordered.map((step, idx) => {
        const exec = execMap.get(step.id);
        return (
          <div
            key={step.id}
            className={cn(
              'flex items-center gap-3 rounded px-3 py-2 text-sm',
              exec?.status === 'success' && 'bg-green-50 dark:bg-green-950/20',
              exec?.status === 'failed' && 'bg-red-50 dark:bg-red-950/20',
              !exec && 'bg-muted/30',
            )}
          >
            <span className="w-6 text-center text-xs text-muted-foreground">{idx + 1}</span>
            <StepTypeIcon type={step.type} />
            <div className="flex-1">
              <span className="font-mono text-xs">{step.id}</span>
              {step.label && <span className="ml-2 text-xs text-muted-foreground">— {step.label}</span>}
            </div>
            <Badge variant="outline" className="text-[10px]">{step.type}</Badge>
            {step.next && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <ArrowRight className="h-3 w-3" /> {step.next}
              </span>
            )}
            {step.onTrue && (
              <span className="text-xs text-green-600">T→{step.onTrue}</span>
            )}
            {step.onFalse && (
              <span className="text-xs text-red-600">F→{step.onFalse}</span>
            )}
            {exec && (
              <span className="text-xs text-muted-foreground">{exec.durationMs}ms</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepTypeIcon({ type }: { type: string }) {
  const iconClass = 'h-4 w-4';
  switch (type) {
    case 'condition': return <GitBranch className={cn(iconClass, 'text-blue-500')} />;
    case 'action': return <Zap className={cn(iconClass, 'text-orange-500')} />;
    case 'notify': return <ArrowRight className={cn(iconClass, 'text-purple-500')} />;
    case 'wait': return <Clock className={cn(iconClass, 'text-gray-500')} />;
    case 'parallel': return <GitBranch className={cn(iconClass, 'text-indigo-500')} />;
    case 'script': return <Code className={cn(iconClass, 'text-green-500')} />;
    default: return <Play className={cn(iconClass, 'text-muted-foreground')} />;
  }
}

// ─── Default Template ──────────────────────────────

const DEFAULT_DEFINITION = JSON.stringify(
  {
    name: 'new-workflow',
    description: 'A new workflow',
    trigger: { type: 'manual' },
    steps: [
      {
        id: 'step1',
        type: 'log',
        label: 'Start',
        config: { message: 'Workflow started with input: {{entityId}}', level: 'info' },
        next: 'step2',
      },
      {
        id: 'step2',
        type: 'condition',
        label: 'Check amount',
        config: { field: 'amount', op: 'gt', value: 1000 },
        onTrue: 'step3',
        onFalse: 'step4',
      },
      {
        id: 'step3',
        type: 'notify',
        label: 'High value alert',
        config: { channel: 'email', template: 'high-value-alert' },
      },
      {
        id: 'step4',
        type: 'log',
        label: 'Low value — done',
        config: { message: 'Low value, no action needed', level: 'info' },
      },
    ],
    entryStep: 'step1',
  },
  null,
  2,
);
